import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    text: 'Al realizar una compra en nuestro sitio, recopilamos únicamente la información necesaria para procesar su pedido: nombre, dirección de correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito en nuestros servidores.'
  }, {
    heading: 'Uso de la Información',
    text: 'Utilizamos su información personal exclusivamente para: procesar y entregar su compra, enviar confirmaciones de pedido, y comunicarnos con usted en caso de ser necesario respecto a su compra. No vendemos, alquilamos ni compartimos su información personal con terceros.'
  }, {
    heading: 'Protección de Datos',
    text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger su información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
  }, {
    heading: 'Cookies',
    text: 'Nuestro sitio puede utilizar cookies esenciales para el funcionamiento básico del sitio web y para procesar transacciones. No utilizamos cookies de rastreo publicitario.'
  }, {
    heading: 'Sus Derechos',
    text: 'Usted tiene derecho a acceder, rectificar o eliminar sus datos personales en cualquier momento. Para ejercer estos derechos, puede comunicarse con nosotros a través de los canales indicados en su confirmación de compra.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Descripción del Producto',
    text: '"Legado en Vida: El Kit de No Me Olvides" es un producto digital descargable en formato PDF. Es una herramienta organizativa diseñada para ayudarle a documentar información importante para sus seres queridos. Este producto NO constituye asesoría legal, financiera ni médica.'
  }, {
    heading: 'Compra y Entrega',
    text: 'Al completar su compra, recibirá acceso inmediato para descargar el producto digital. El enlace de descarga se enviará a la dirección de correo electrónico proporcionada durante la compra.'
  }, {
    heading: 'Política de Reembolso',
    text: 'Debido a la naturaleza digital del producto, todas las ventas son finales una vez que se ha descargado el archivo. Si experimenta problemas técnicos con la descarga, nos comprometemos a asistirle hasta que pueda acceder al producto correctamente.'
  }, {
    heading: 'Uso Permitido',
    text: 'El producto es para uso personal y familiar únicamente. No está permitido reproducir, distribuir, vender o compartir el contenido del kit con fines comerciales. Puede imprimir copias para uso personal y de sus familiares directos.'
  }, {
    heading: 'Limitación de Responsabilidad',
    text: 'Este producto es una herramienta organizativa complementaria. No sustituye la asesoría de profesionales legales, financieros o médicos. No nos hacemos responsables de decisiones tomadas basándose únicamente en la información organizada mediante este kit.'
  }, {
    heading: 'Modificaciones',
    text: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación en el sitio web.'
  }]
};
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.25
  }} data-id="element-41">
      {/* Backdrop */}
      <motion.div className="absolute inset-0 bg-charcoal/60 backdrop-blur-sm" onClick={onClose} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-42" />

      {/* Modal */}
      <motion.div className="relative bg-card rounded-2xl shadow-warm-lg max-w-2xl w-full max-h-[85vh] overflow-hidden border border-sage/10" initial={{
      opacity: 0,
      y: 30,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} transition={{
      duration: 0.3,
      ease: [0.21, 0.47, 0.32, 0.98]
    }} role="dialog" aria-modal="true" aria-label={content.title} data-id="element-43">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-sage/10 px-6 md:px-8 py-5 flex items-center justify-between z-10" data-id="element-44">
          <h2 className="font-serif text-2xl text-charcoal font-semibold" data-id="element-45">
            {content.title}
          </h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full flex items-center justify-center text-muted hover:text-charcoal hover:bg-sage/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sage" aria-label="Cerrar" data-id="element-46">
            <XIcon className="w-5 h-5" data-id="element-47" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 md:px-8 py-6 overflow-y-auto max-h-[calc(85vh-80px)]" data-id="element-48">
          <div className="space-y-8" data-id="element-49">
            {content.sections.map((section, index) => <div key={index} data-id="element-50">
                <h3 className="font-serif text-lg text-charcoal font-semibold mb-2" data-id="element-51">
                  {section.heading}
                </h3>
                <p className="text-muted leading-relaxed" data-id="element-52">{section.text}</p>
              </div>)}
          </div>

          <div className="mt-10 pt-6 border-t border-sage/10" data-id="element-53">
            <p className="text-sm text-muted/70 text-center" data-id="element-54">
              Última actualización:{' '}
              {new Date().toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-charcoal text-cream/80 py-12 px-6 text-center" data-id="element-55">
        <div className="max-w-4xl mx-auto" data-id="element-56">
          <p className="font-serif text-2xl text-cream mb-6 italic" data-id="element-57">
            Legado en Vida
          </p>

          {/* Legal links */}
          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-58">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-cream/60 hover:text-cream transition-colors underline underline-offset-4 decoration-cream/30 hover:decoration-cream/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-cream/50 rounded" data-id="element-59">
              Política de Privacidad
            </button>
            <span className="text-cream/30" data-id="element-60">•</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-cream/60 hover:text-cream transition-colors underline underline-offset-4 decoration-cream/30 hover:decoration-cream/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-cream/50 rounded" data-id="element-61">
              Términos y Condiciones
            </button>
          </div>

          <p className="text-sm mb-4" data-id="element-62">
            © {new Date().getFullYear()} Legado en Vida. Todos los derechos
            reservados.
          </p>
          <p className="text-xs text-cream/50 max-w-2xl mx-auto" data-id="element-63">
            Este producto es una herramienta organizativa y no sustituye el
            consejo legal profesional. Para asuntos legales complejos, consulte
            siempre con un abogado especializado en su país.
          </p>
        </div>
      </footer>

      {/* Legal Modals */}
      <AnimatePresence data-id="element-64">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-65" />}
      </AnimatePresence>
    </>;
}